/** Normalize problem text so we can refuse the same statement twice. */
export function fingerprintStatement(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 480);
}

export function statementsOverlap(a, b) {
  const fa = fingerprintStatement(a);
  const fb = fingerprintStatement(b);
  if (!fa || !fb) return false;
  if (fa === fb) return true;
  const wa = new Set(fa.split(" ").filter((w) => w.length > 3));
  const wb = fb.split(" ").filter((w) => w.length > 3);
  if (!wa.size || !wb.length) return fa.slice(0, 80) === fb.slice(0, 80);
  let hit = 0;
  wb.forEach((w) => {
    if (wa.has(w)) hit += 1;
  });
  return hit / Math.min(wa.size, wb.length) >= 0.72;
}

const LS_USED = "ap_used_problem_fingerprints";

export function loadLocalUsedFingerprints() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_USED) || "[]");
    return Array.isArray(raw) ? raw.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
}

export function claimLocalFingerprint(text, meta = {}) {
  const fp = fingerprintStatement(text);
  if (!fp) return "";
  const next = Array.from(new Set([...loadLocalUsedFingerprints(), fp])).slice(-400);
  try {
    localStorage.setItem(LS_USED, JSON.stringify(next));
    const extra = {
      fingerprint: fp,
      claimedAt: Date.now(),
      ...meta,
    };
    const bag = JSON.parse(localStorage.getItem(`${LS_USED}_meta`) || "{}");
    bag[fp] = extra;
    localStorage.setItem(`${LS_USED}_meta`, JSON.stringify(bag));
  } catch {
    /* ignore quota */
  }
  return fp;
}

export function fingerprintsFromRecords(records = []) {
  const out = [];
  (records || []).forEach((r) => {
    const blob = [r?.title, r?.description, r?.statement, r?.problemStatement]
      .filter(Boolean)
      .join(" ");
    const fp = r?.fingerprint || fingerprintStatement(blob);
    if (fp) out.push(fp);
  });
  return out;
}

export function isStatementTaken(text, usedFingerprints = []) {
  const fp = fingerprintStatement(text);
  if (!fp) return false;
  if (usedFingerprints.includes(fp)) return true;
  return usedFingerprints.some((u) => statementsOverlap(fp, u));
}

function firstText(...vals) {
  for (const v of vals) {
    const s = String(v == null ? "" : v).trim();
    if (s) return s;
  }
  return "";
}

/** YC batch W24 / S13 → calendar year. */
export function parseYearFromText(text) {
  const s = String(text || "");
  const batch = s.match(/\b([WS])\s*'?(\d{2})\b/i);
  if (batch) {
    const yy = Number(batch[2]);
    const year = 2000 + yy;
    if (year >= 2005 && year <= 2030) {
      return { year: String(year), batch: `${batch[1].toUpperCase()}${String(yy).padStart(2, "0")}` };
    }
  }
  const labeled = s.match(/\b(?:yc|y combinator|unicorn|founded|batch|class of)\s*[:\-]?\s*(20[0-2]\d)\b/i);
  if (labeled) return { year: labeled[1], batch: "" };
  const y = s.match(/\b(20(?:0[5-9]|1\d|2[0-6]))\b/);
  if (y) return { year: y[1], batch: "" };
  return { year: "", batch: "" };
}

const COMPANY_YEARS = [
  ["airbnb", "Airbnb", "2008", "yc"],
  ["stripe", "Stripe", "2010", "yc"],
  ["dropbox", "Dropbox", "2007", "yc"],
  ["doordash", "DoorDash", "2013", "yc"],
  ["instacart", "Instacart", "2012", "yc"],
  ["coinbase", "Coinbase", "2012", "yc"],
  ["reddit", "Reddit", "2005", "yc"],
  ["twitch", "Twitch", "2011", "unicorn"],
  ["notion", "Notion", "2013", "unicorn"],
  ["figma", "Figma", "2012", "unicorn"],
  ["canva", "Canva", "2012", "unicorn"],
  ["openai", "OpenAI", "2015", "unicorn"],
  ["anthropic", "Anthropic", "2021", "unicorn"],
  ["brex", "Brex", "2017", "yc"],
  ["rippling", "Rippling", "2016", "yc"],
  ["gusto", "Gusto", "2012", "yc"],
  ["pagerduty", "PagerDuty", "2010", "yc"],
  ["gitlab", "GitLab", "2015", "unicorn"],
  ["databricks", "Databricks", "2013", "unicorn"],
  ["snowflake", "Snowflake", "2012", "unicorn"],
  ["uber", "Uber", "2009", "unicorn"],
  ["lyft", "Lyft", "2012", "unicorn"],
  ["byju", "BYJU'S", "2011", "unicorn"],
  ["razorpay", "Razorpay", "2014", "yc"],
  ["meesho", "Meesho", "2015", "unicorn"],
  ["zerodha", "Zerodha", "2010", "unicorn"],
  ["cred", "CRED", "2018", "unicorn"],
  ["phonepe", "PhonePe", "2015", "unicorn"],
  ["swiggy", "Swiggy", "2014", "unicorn"],
  ["zomato", "Zomato", "2008", "unicorn"],
];

function inferCompany(text) {
  const blob = String(text || "").toLowerCase();
  return COMPANY_YEARS.find(([key]) => blob.includes(key)) || null;
}

export function parseCompanyFromSource(source) {
  const s = String(source || "").trim();
  if (!s) return "";
  const em = s.split(/\s*[—–-]\s*/);
  if (em.length >= 2) {
    let right = em.slice(1).join(" — ").trim();
    right = right.replace(/\s*\([^)]*\)\s*$/, "").trim();
    right = right.replace(/\s*\/\s*.*$/, "").trim();
    if (right && !/^(yc|unicorn|offline|fallback)/i.test(right)) return right;
  }
  const m = s.match(/\b(?:YC|Unicorn)[^A-Za-z0-9]+([A-Z][\w.&'+-]{1,40})/);
  return m ? m[1] : "";
}

const STOP = new Set([
  "that", "this", "with", "from", "your", "their", "have", "been", "will", "into",
  "using", "style", "without", "every", "about", "which", "where", "when", "what",
  "make", "made", "than", "also", "only", "more", "most", "such", "over", "under",
  "for", "the", "and", "are", "was", "were", "they", "them",
]);

function analogTokens(text) {
  return fingerprintStatement(text)
    .split(" ")
    .filter((w) => w.length > 3 && !STOP.has(w));
}

/** Closest YC / Unicorn company analog for a student problem. */
export function matchBestAnalog(text, ideas = []) {
  const pool = (ideas || []).filter((i) => i && (i.title || i.source || i.company));
  if (!pool.length) return null;
  const withOrigin = pool.filter(
    (i) => i.year || i.company || parseCompanyFromSource(i.source)
  );
  const list = withOrigin.length ? withOrigin : pool;
  const nset = new Set(analogTokens(text));
  const lower = String(text || "").toLowerCase();
  let best = list[0];
  let score = -1;
  list.forEach((idea) => {
    const blob = `${idea.title || ""} ${idea.company || idea.name || ""} ${idea.source || ""} ${idea.oneLiner || idea.description || ""}`;
    const it = analogTokens(blob);
    let hit = 0;
    it.forEach((w) => {
      if (nset.has(w)) hit += 1;
    });
    const co = String(idea.company || parseCompanyFromSource(idea.source) || "").toLowerCase();
    const coKey = co.split(/[^a-z0-9]+/)[0];
    if (coKey && coKey.length > 2 && lower.includes(coKey)) hit += 10;
    const s = hit + (it.length ? hit / it.length : 0);
    if (s > score) {
      score = s;
      best = idea;
    }
  });
  return best;
}

/** Company / year / YC-or-Unicorn details from verify, generate, or inspiration. */
export function extractProvenance(report = {}, idea = null, extraText = "") {
  const kindRaw = firstText(
    report.sourceKind,
    report.origin,
    report.frameworkSource,
    report.inspiredBy,
    idea?.kind,
    idea?.sourceKind
  ).toLowerCase();
  const yc =
    report.fitsYC === true ||
    /yc|y combinator|ycombinator/.test(kindRaw) ||
    /y combinator|yc\b/.test(String(idea?.source || "").toLowerCase());
  const uni =
    report.fitsUnicorn === true ||
    /unicorn/.test(kindRaw) ||
    /unicorn/.test(String(idea?.source || "").toLowerCase());

  const blob = [
    extraText,
    report.statement,
    report.verdict,
    idea?.title,
    idea?.source,
    idea?.company,
  ].filter(Boolean).join(" ");
  const parsed = parseYearFromText(blob);
  const hinted = inferCompany(blob);

  const rawSource = firstText(report.sourceLabel, idea?.source, report.analogSource);
  let sourceLabel = rawSource;
  if (/unicorn/i.test(rawSource) && !/y combinator|\byc\b/i.test(rawSource)) {
    sourceLabel = "Unicorn Company";
  } else if (/^yc\b|y combinator/i.test(rawSource)) {
    sourceLabel = "Y Combinator";
  } else if (!sourceLabel) {
    if (yc && uni) sourceLabel = "Y Combinator + Unicorn";
    else if (yc) sourceLabel = "Y Combinator";
    else if (uni) sourceLabel = "Unicorn Company";
    else if (hinted?.[3] === "yc") sourceLabel = "Y Combinator";
    else if (hinted?.[3] === "unicorn") sourceLabel = "Unicorn Company";
    else sourceLabel = "Y Combinator or Unicorn Company";
  }

  const year = firstText(
    report.year,
    report.batchYear,
    report.sourceYear,
    idea?.year,
    parsed.year,
    hinted?.[2]
  );
  const batch = firstText(report.batch, report.ycBatch, report.season, idea?.batch, parsed.batch);
  const yearLabel = [year, batch ? `YC ${batch}` : ""].filter(Boolean).join(" · ") || "Year not listed";

  return {
    sourceKind: yc && !uni ? "yc" : uni && !yc ? "unicorn" : yc && uni ? "both" : (hinted?.[3] || kindRaw || ""),
    sourceLabel,
    company: firstText(
      report.company,
      report.companyName,
      report.inspiredCompany,
      report.referenceCompany,
      report.workingCompany,
      idea?.company,
      idea?.name,
      idea?.org,
      parseCompanyFromSource(rawSource),
      parseCompanyFromSource(idea?.source),
      hinted?.[1]
    ),
    year,
    yearLabel,
    batch,
    analogTitle: firstText(report.analogTitle, idea?.title),
    analogSource: firstText(report.analogSource, idea?.source),
    originWhy: firstText(report.originWhy, report.whyThisCompany),
    workingOn: firstText(
      report.workingOn,
      report.companyOneLiner,
      report.oneLiner,
      report.whatTheyDo,
      idea?.oneLiner,
      idea?.description,
      idea?.blurb
    ),
    url: firstText(report.sourceUrl, report.url, report.website, idea?.url, idea?.website),
    stage: firstText(report.stage, report.valuation, idea?.stage, idea?.valuation),
    isLive: report.isLive === true || idea?.isLive === true,
    location: firstText(report.location, report.hq, idea?.location),
  };
}

export function matchInspiration(text, ideas = []) {
  return matchBestAnalog(text, ideas);
}

function kindScore(item, re) {
  return re.test(`${item.sourceKind || ""} ${item.source || ""} ${item.sourceLabel || ""} ${item.kind || ""}`) ? 1 : 0;
}

/** Split verify report into verified vs not-verified facts (never hide gaps). */
export function buildVerificationChecklist(report = {}, origin = {}) {
  const items = [];
  const add = (id, group, label, ok, detail = "") => {
    items.push({
      id,
      group,
      label,
      ok: !!ok,
      detail: String(detail || "").trim(),
    });
  };

  const ycChecks = Array.isArray(report.yc) ? report.yc : [];
  const uniChecks = Array.isArray(report.unicorn) ? report.unicorn : [];
  const ycN = report.ycPassCount ?? ycChecks.filter((x) => x.pass).length;
  const uniN = report.unicornPassCount ?? uniChecks.filter((x) => x.pass).length;
  const ycTotal = report.ycTotal || ycChecks.length || 5;
  const uniTotal = report.unicornTotal || uniChecks.length || 5;
  const tech = (report.matchedTech || report.matchedTechnologies || []).filter(Boolean);
  const industry = (report.matchedVerticals || report.matchedIndustries || []).filter(Boolean);
  const trackOk = !!(report.fitsYcOrUnicorn || report.fitsYC || report.fitsUnicorn);
  const yearOk = Boolean(origin.year && String(origin.year) !== "Year not listed");
  const sourceOk = Boolean(
    origin.sourceKind === "yc" ||
      origin.sourceKind === "unicorn" ||
      origin.sourceKind === "both" ||
      (/y combinator|unicorn/i.test(String(origin.sourceLabel || "")) &&
        !/or unicorn company$/i.test(String(origin.sourceLabel || "")))
  );

  add("track", "Gate", "Y Combinator or Unicorn Company (need one)", trackOk, trackOk
    ? (report.fitsYC && report.fitsUnicorn ? "Both tracks passed" : report.fitsYC ? "Y Combinator passed" : "Unicorn Company passed")
    : "Neither track passed");
  add("yc", "Gate", "Y Combinator fit", !!report.fitsYC, `${ycN}/${ycTotal} checks`);
  add("unicorn", "Gate", "Unicorn Company fit", !!report.fitsUnicorn, `${uniN}/${uniTotal} checks`);
  add("thrust", "Gate", "SNS thrust area (technology or industry)", !!report.fits77,
    [...tech, ...industry].join(", ") || "No thrust match");
  add("source", "Origin", "Source identified", sourceOk, origin.sourceLabel || "Not identified");
  add("company", "Origin", "Company working on this", !!origin.company, origin.company || "Not found");
  add("year", "Origin", "Year", yearOk, origin.year || origin.yearLabel || "Not found");
  const isYcOrigin = origin.sourceKind === "yc" || origin.sourceKind === "both";
  if (isYcOrigin && origin.batch) {
    add("batch", "Origin", "YC batch", true, origin.batch);
  }
  if (origin.workingOn) {
    add("working", "Origin", "What the company works on", true, origin.workingOn);
  }
  add("ai", "Content", "Contains AI", report.containsAI === true, report.containsAI === true ? "Yes" : "No / not detected");

  ycChecks.forEach((c, i) => {
    add(`yc-${c.id || i}`, "Y Combinator checks", c.label || `YC check ${i + 1}`, !!c.pass, c.note || c.why || "");
  });
  uniChecks.forEach((c, i) => {
    add(`un-${c.id || i}`, "Unicorn checks", c.label || `Unicorn check ${i + 1}`, !!c.pass, c.note || c.why || "");
  });

  return {
    overall: !!report.verified,
    items,
    verified: items.filter((i) => i.ok),
    notVerified: items.filter((i) => !i.ok),
  };
}

export function sortProblemOptions(list, sortBy = "relevance") {
  const rows = [...(list || [])];
  if (sortBy === "year") {
    rows.sort((a, b) => Number(b.year || a.year || 0) - Number(a.year || b.year || 0));
  } else if (sortBy === "yc") {
    rows.sort((a, b) => kindScore(b, /yc|combinator/i) - kindScore(a, /yc|combinator/i));
  } else if (sortBy === "unicorn") {
    rows.sort((a, b) => kindScore(b, /unicorn/i) - kindScore(a, /unicorn/i));
  }
  return rows;
}
